import React, { FC } from "react";
import { Avatar, Box, Typography } from "@mui/material";
import Badge from "../../../../assets/hamaspik/Badge";
import { PifApprovalRecord } from "../types";
import { bandFillStyle, bandPercent } from "./approvalHelpers";
import "./PifApprovalReview.scss";

interface PifApprovalRecordPanelProps {
  record: PifApprovalRecord;
}

const PifApprovalRecordPanel: FC<PifApprovalRecordPanelProps> = ({ record }) => (
  <Box className="pif-approval-record">
    <Box className="pif-approval-record__header">
      <Avatar
        src={record.profileImage || undefined}
        alt={record.applicantName}
        sx={{ width: 56, height: 56, bgcolor: "secondary.main", color: "#fff", fontWeight: 700, fontSize: 18, flex: "none" }}
      >
        {record.applicantInitials}
      </Avatar>
      <Box className="pif-approval-record__header-name">
        <Typography className="pif-approval-record__name">{record.applicantName}</Typography>
        <Typography className="pif-approval-record__meta">{record.applicantMeta}</Typography>
      </Box>
      <Box className="pif-approval-record__header-tags">
        <Badge variant="primary" size="sm">
          {record.positionTag}
        </Badge>
        <Typography className="pif-approval-record__emp-id">{record.employeeIdLabel}</Typography>
      </Box>
    </Box>

    {record.sections.map((sec) => (
      <Box key={sec.title} className="pif-approval-record__section">
        <Box className="pif-approval-record__section-head">
          <Typography className="pif-approval-record__section-title">{sec.title}</Typography>
          <Typography className="pif-approval-record__section-note">{sec.note}</Typography>
        </Box>
        <Box className="pif-approval-record__section-grid">
          {sec.rows.map((r) => (
            <Box key={r.k} className="pif-approval-record__cell">
              <Typography className="pif-approval-record__cell-k">{r.k}</Typography>
              <Box className="pif-approval-record__cell-vrow">
                <Typography className="pif-approval-record__cell-v">{r.v}</Typography>
                {r.pill && (
                  <Badge variant={r.pillTone === "warn" ? "warning" : r.pillTone === "ok" ? "success" : "neutral"} size="sm">
                    {r.pill}
                  </Badge>
                )}
              </Box>
              {r.hint && <Typography className="pif-approval-record__cell-hint">{r.hint}</Typography>}
            </Box>
          ))}
        </Box>
        {sec.band && (
          <Box className="pif-approval-record__band">
            <Box className="pif-approval-record__band-head">
              <Typography className="pif-approval-record__band-label">PAY BAND</Typography>
              <Typography className="pif-approval-record__band-range">
                Position ${sec.band.positionMin}–${sec.band.positionMax} · hiring ${sec.band.hiringMin}–${sec.band.hiringMax}
              </Typography>
            </Box>
            <Box className="pif-approval-record__band-track">
              <Box className="pif-approval-record__band-fill" style={bandFillStyle(sec.band)} />
              <Box
                className={`pif-approval-record__band-marker${sec.band.outOfBand ? " pif-approval-record__band-marker--warn" : ""}`}
                style={{ left: `${bandPercent(sec.band.rate, sec.band.positionMin, sec.band.positionMax)}%` }}
              />
            </Box>
            <Box className="pif-approval-record__band-scale">
              <span>${sec.band.positionMin}</span>
              <span>${sec.band.positionMax}</span>
            </Box>
            <Typography className={`pif-approval-record__band-msg${sec.band.outOfBand ? " pif-approval-record__band-msg--warn" : ""}`}>
              {sec.band.outOfBand
                ? `$${sec.band.rate.toFixed(2)} is above the hiring band but inside the position range. Approving it is a judgment call, not an exception request.`
                : `$${sec.band.rate.toFixed(2)} is inside the hiring band — no extra scrutiny needed.`}
            </Typography>
          </Box>
        )}
      </Box>
    ))}
  </Box>
);

export default PifApprovalRecordPanel;
